/**
 * KST(Asia/Seoul) 고정 — 기기 타임존·로케일에 기대지 않는다.
 *
 * ⚠️ `toLocaleString(… { timeZone: 'Asia/Seoul' })` 은 대부분 기기에서 맞지만,
 *    지역·날짜형식 설정에 따라 일부 기기가 미국식으로 폴백한다. 그러면 문자열
 *    날짜 비교가 깨진다. 2026-07-08 에 DCMC 에서 실제로 수기·부재 등록이
 *    막혔던 원인이 그것이다 (apps/client/src/lib/kst.ts 참고).
 *
 * → UTC+9 로 시프트한 뒤 UTC 필드를 직접 읽는다. 설정 영향 0.
 *
 * 판정(지각·조퇴)도 여기에 기댄다. 조직의 workStartTime "09:00" 은 KST 벽시계
 * 시각이므로, 비교 대상도 반드시 KST 여야 한다. 기기 타임존을 쓰면 해외에
 * 있거나 시계가 틀어진 사람의 판정이 어긋난다.
 */

const KST_OFFSET_MS = 9 * 60 * 60 * 1000

export interface KstParts {
  year: number
  /** 1~12 */
  month: number
  day: number
  hours: number
  minutes: number
}

export function kstParts(d: Date | string | number): KstParts {
  const ms = typeof d === 'object' ? d.getTime() : new Date(d).getTime()
  const s = new Date(ms + KST_OFFSET_MS)
  return {
    year: s.getUTCFullYear(),
    month: s.getUTCMonth() + 1,
    day: s.getUTCDate(),
    hours: s.getUTCHours(),
    minutes: s.getUTCMinutes(),
  }
}

/** KST 기준 YYYY-MM-DD */
export function kstDateStr(d: Date | string | number = Date.now()): string {
  const p = kstParts(d)
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`
}

/** KST 기준 YYYY.MM.DD — 서류(계약서·급여명세서) 목록 표기용 */
export function kstDotDateStr(d: Date | string | number): string {
  const p = kstParts(d)
  return `${p.year}.${String(p.month).padStart(2, '0')}.${String(p.day).padStart(2, '0')}`
}

/** KST 기준 YYYY-MM — 급여월 등 월 단위 키 */
export function kstMonthStr(d: Date | string | number = Date.now()): string {
  const p = kstParts(d)
  return `${p.year}-${String(p.month).padStart(2, '0')}`
}

/** KST 기준 HH:mm */
export function kstTimeStr(d: Date | string | number): string {
  const p = kstParts(d)
  return `${String(p.hours).padStart(2, '0')}:${String(p.minutes).padStart(2, '0')}`
}

/** KST 기준 "8월 4일" — 상대 표기("전일")는 쓰지 않는다 (DCMC 14-1) */
export function kstMonthDayStr(d: Date | string | number): string {
  const p = kstParts(d)
  return `${p.month}월 ${p.day}일`
}

/** 자정 기준 분 — 판정 비교용 */
export function kstMinutesOfDay(d: Date | string | number): number {
  const p = kstParts(d)
  return p.hours * 60 + p.minutes
}
